import {
  CourseKitProvider,
  createCourseQueryClient,
  LoginPage,
  PageLoading,
  RouteErrorPage,
  SiteLayout,
  Toaster,
  loginPath,
  useSession,
  useSiteCopy,
  type CourseSiteBrand,
  type SiteNavLink,
} from "@bodemleven/course-kit/react";
import type { QueryClient } from "@tanstack/react-query";
import { Microscope, UsersRound } from "lucide-react";
import { useState, type ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  type DataRouter,
} from "react-router";
import { useCommonCopy } from "../i18n/useCommonCopy.ts";
import { useShellCopy } from "../i18n/useShellCopy.ts";
import { paths, WORKSPACE_BASE } from "../paths.ts";
import { CurrentUserProvider } from "./current-user.tsx";
import { HomePage } from "./HomePage.tsx";
import { NotFoundPage } from "./StatusPages.tsx";
import { workspaceRoutes } from "./workspace-routes.tsx";

/** Kit header/footer with the workspace link for signed-in users and the users page for admins. */
const ResearchLayout = (): ReactNode => {
  const { nav } = useCommonCopy();
  const navLinks: SiteNavLink[] = [
    { to: WORKSPACE_BASE, label: nav.workspace, icon: Microscope, access: "user" },
  ];
  const accountLinks: SiteNavLink[] = [
    { to: paths.users(), label: nav.users, icon: UsersRound, access: "admin" },
  ];
  return <SiteLayout navLinks={navLinks} accountLinks={accountLinks} />;
};

const WorkspaceLoading = (): ReactNode => <PageLoading label={useCommonCopy().status.loading} />;

/** `/onderzoek/*`: signed-in users only. */
const WorkspaceGate = (): ReactNode => {
  const common = useCommonCopy();
  const { user, loading } = useSession();
  const { pathname, search } = useLocation();
  if (loading) return <PageLoading label={common.status.loading} />;
  if (!user) return <Navigate to={loginPath(`${pathname}${search}`)} replace />;
  return (
    <CurrentUserProvider user={user}>
      <Outlet />
    </CurrentUserProvider>
  );
};

const ResearchLogin = (): ReactNode => {
  const { login } = useShellCopy();
  return <LoginPage intro={{ signIn: login.signInIntro, register: login.registerIntro }} />;
};

const SiteToaster = (): ReactNode => {
  const { common } = useSiteCopy();
  return <Toaster label={common.notifications} dismissLabel={common.dismiss} />;
};

/**
 * A data router (not <BrowserRouter>): the analyst editors guard unsaved changes with
 * useBlocker, SiteLayout restores scroll positions and workspace pages prefetch their
 * data in loaders. The pathless route keeps header and footer around a page that fails;
 * the root error element only shows when the layout itself fails.
 */
const createRouter = (queryClient: QueryClient): DataRouter =>
  createBrowserRouter([
    {
      element: <ResearchLayout />,
      errorElement: <RouteErrorPage standalone />,
      children: [
        {
          errorElement: <RouteErrorPage />,
          children: [
            { index: true, element: <HomePage /> },
            { path: "inloggen", element: <ResearchLogin /> },
            {
              path: WORKSPACE_BASE.slice(1),
              element: <WorkspaceGate />,
              // Opening the workspace directly: shown while its code and data load.
              hydrateFallbackElement: <WorkspaceLoading />,
              children: workspaceRoutes(queryClient),
            },
            { path: "*", element: <NotFoundPage /> },
          ],
        },
      ],
    },
  ]);

/** The research site: public home, login and the signed-in /onderzoek workspace. */
export const ResearchSite = ({ brand }: { brand: CourseSiteBrand }): ReactNode => {
  // One cache for the pages and the route loaders that fill it.
  const [queryClient] = useState(createCourseQueryClient);
  const [router] = useState(() => createRouter(queryClient));
  return (
    <CourseKitProvider brand={brand} queryClient={queryClient}>
      <RouterProvider router={router} />
      <SiteToaster />
    </CourseKitProvider>
  );
};
